import React, { useState } from "react";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridValueGetterParams,
    useGridApiRef,
} from "@mui/x-data-grid";
import { SystemMailCampaign } from "../../model/SystemMailCampaign";
import { SystemMailCampaignReceiverGroup } from "../../model/SystemMailCampaign";
import classes from "../../styling/components/Table.module.css";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, IconButton } from "@mui/material";
import {
    getSystemMailCampaigns,
    updateSystemMailCampaignStatus,
    deleteSystemMailCampaign,
} from "../../api/systemMailCampaignClient";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { Status } from "../../model/Campaign";

interface Props {
    campaigns: SystemMailCampaign[];
}

const SystemMailCampaignTable: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const apiRef = useGridApiRef();
    const navigate = useNavigate();
    const { customerId } = useParams();
    const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
    const [list, setList] = useState<SystemMailCampaign[]>(props.campaigns);
    const [error, setError] = useState<string | null>(null);

    const refreshList = async () => {
        if (!customerId) return;
        try {
            const campaigns = await getSystemMailCampaigns(customerId);
            setList(campaigns);
        } catch {
            setError(t("systemMailCampaign.table.loadError"));
        }
    };

    const confirmChangeStatus = (status: Status, params: GridRenderCellParams) => {
        const callback = async () => {
            const campaignId = params.row.campaignId;
            if (!campaignId || !customerId) return;
            try {
                await updateSystemMailCampaignStatus(campaignId, customerId, status);
                apiRef.current?.updateRows([{ campaignId, status }]);
                await refreshList();
            } catch {
                setError(t("systemMailCampaign.table.statusError"));
            }
        };
        openConfirmationDialog(
            t("systemMailCampaign.table.changeStatusTitle"),
            t("systemMailCampaign.table.changeStatusContent"),
            callback
        );
    };

    const changeStatus = (currentStatus: string, params: GridRenderCellParams) => {
        let newStatus: Status;
        switch (currentStatus) {
            case Status.IN_CONFIGURATION:
                newStatus = Status.READY;
                break;
            case Status.READY:
                newStatus = Status.IN_CONFIGURATION;
                break;
            case Status.RUNNING:
                newStatus = Status.PAUSED;
                break;
            case Status.PAUSED:
                newStatus = Status.RUNNING;
                break;
            default:
                return;
        }
        confirmChangeStatus(newStatus, params);
    };

    const renderStatusButton = (params: GridRenderCellParams) => {
        const status = params.row.status;
        let label = "";
        let isDisabled = true;
        switch (status) {
            case Status.IN_CONFIGURATION:
                label = t("systemMailCampaign.table.markAsReady");
                isDisabled = false;
                break;
            case Status.READY:
                label = t("systemMailCampaign.table.markAsInConfig");
                isDisabled = false;
                break;
            case Status.RUNNING:
                label = t("systemMailCampaign.table.pauseCampaign");
                isDisabled = false;
                break;
            case Status.PAUSED:
                label = t("systemMailCampaign.table.continueCampaign");
                isDisabled = false;
                break;
            case Status.FINISHED:
                label = t("systemMailCampaign.table.cannotChangeFinished");
                break;
            case Status.CANCELLED:
                label = t("systemMailCampaign.table.cannotChangeCancelled");
                break;
            default:
                label = status;
        }
        return (
            <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={isDisabled}
                onClick={() => changeStatus(status, params)}
            >
                {label}
            </Button>
        );
    };

    const confirmDelete = (campaignId: string) => {
        const callback = async () => {
            if (!customerId) return;
            try {
                await deleteSystemMailCampaign(campaignId, customerId);
                setList((prev) => prev.filter((c) => c.campaignId !== campaignId));
            } catch {
                setError(t("systemMailCampaign.table.deleteError"));
            }
        };
        openConfirmationDialog(
            t("systemMailCampaign.table.deleteTitle"),
            t("systemMailCampaign.table.deleteContent"),
            callback
        );
    };

    const statusLabelKey = (value: string) =>
        `campaignTable.currentStatus.${value.toLowerCase().split(" ").join("")}`;

    const columns: GridColDef[] = [
        { field: "name", headerName: t("systemMailCampaign.table.name"), width: 160 },
        {
            field: "receiverGroups",
            headerName: t("campaignTable.receiverGroups"),
            width: 180,
            renderCell: (params: GridRenderCellParams) =>
                (params.value as SystemMailCampaignReceiverGroup[])
                    .map((g) => g.name)
                    .join(", ") || "—",
        },
        {
            field: "changeStatus",
            headerName: t("campaignTable.changeStatus"),
            width: 180,
            renderCell: renderStatusButton,
        },
        {
            field: "status",
            headerName: t("campaignTable.status"),
            width: 140,
            renderCell: (params) => (
                <span>{t(statusLabelKey(params.value as string))}</span>
            ),
        },
        {
            field: "edit",
            headerName: t("campaignTable.edit"),
            width: 70,
            valueGetter: (params: GridValueGetterParams) => params.row.campaignId,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    aria-label="edit"
                    onClick={() => navigate(String(params.value))}
                >
                    <EditIcon />
                </IconButton>
            ),
        },
        {
            field: "delete",
            headerName: t("systemMailCampaign.table.delete"),
            width: 70,
            valueGetter: (params: GridValueGetterParams) => params.row.campaignId,
            renderCell: (params: GridRenderCellParams) => {
                const campaign = params.row as SystemMailCampaign;
                const canDelete =
                    campaign.status === Status.IN_CONFIGURATION ||
                    campaign.status === Status.CANCELLED;
                return (
                    <IconButton
                        aria-label="delete"
                        disabled={!canDelete}
                        onClick={() => confirmDelete(params.value as string)}
                    >
                        <DeleteIcon />
                    </IconButton>
                );
            },
        },
    ];

    return (
        <>
            {confirmationDialog}
            <div className={classes.campaigntable}>
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{ mb: 1 }}
                    >
                        {error}
                    </Alert>
                )}
                <DataGrid
                    rows={list}
                    getRowId={(row) => row.campaignId ?? ""}
                    columns={columns}
                    apiRef={apiRef}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 5 },
                        },
                    }}
                    pageSizeOptions={[5, 10]}
                />
            </div>
        </>
    );
};

export default SystemMailCampaignTable;
